import { useCallback, useEffect, useState } from "react";
import { Mail, Phone } from "lucide-react";
import { adminApi } from "../../lib/adminApi";
import { Card, PageTitle, Btn, run, fmtDateTime } from "../../components/admin/AdminUI";

const FILTERS = [["all", "All"], ["unread", "Unread"], ["read", "Read"]];

export default function Messages() {
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const load = useCallback(() => adminApi.get("/admin/messages", { params: { status: filter, page } }).then((r) => setData(r.data)), [filter, page]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (m) => {
    const r = await run(() => adminApi.patch(`/admin/messages/${m.id}`, { read: m.status !== "read" }));
    if (r) load();
  };

  return (
    <div data-testid="admin-messages">
      <PageTitle eyebrow="Inbox" title="Messages" actions={data && <span className="text-sm text-slate-600" data-testid="messages-unread-count">{data.unread} unread</span>} />
      <div className="flex gap-1 mb-5">
        {FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => { setFilter(v); setPage(1); }} data-testid={`messages-filter-${v}`}
            className={`h-9 px-4 text-sm border ${filter === v ? "bg-amber-500 border-amber-500 text-slate-900 font-semibold" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>
      {!data ? <p className="text-slate-500">Loading…</p> : data.items.length === 0 ? <Card><p className="text-sm text-slate-500" data-testid="messages-empty">No messages.</p></Card> : (
        <div className="space-y-3" data-testid="messages-list">
          {data.items.map((m) => {
            const unread = m.status !== "read";
            return (
              <article key={m.id} className={`panel p-5 ${unread ? "border-l-4 !border-l-amber-500" : ""}`} data-testid={`message-${m.id}`} data-unread={unread}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{m.name}</span>
                      {unread && <span className="text-[10px] font-mono uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5">New</span>}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-4 text-xs text-slate-600">
                      <a href={`mailto:${m.email}`} className="flex items-center gap-1 hover:text-brand-600"><Mail className="w-3 h-3" />{m.email}</a>
                      {m.phone && <a href={`tel:${m.phone}`} className="flex items-center gap-1 hover:text-brand-600"><Phone className="w-3 h-3" />{m.phone}</a>}
                      <span>{fmtDateTime(m.created_at)}</span>
                    </div>
                  </div>
                  <Btn variant="ghost" onClick={() => toggle(m)} data-testid={`message-toggle-${m.id}`}>{unread ? "Mark read" : "Mark unread"}</Btn>
                </div>
                <p className="mt-3 text-sm text-slate-800 whitespace-pre-wrap">{m.message}</p>
              </article>
            );
          })}
          <div className="flex items-center justify-between text-sm text-slate-600 pt-2">
            <span>Page {data.page} of {data.pages}</span>
            <div className="flex gap-2">
              <Btn variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="messages-prev">Prev</Btn>
              <Btn variant="ghost" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="messages-next">Next</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
